import {
  BERTH_SIDES,
  CARGO_PORT_OPERATIONS,
  CARGO_UNITS,
  cargoRemaining,
  DAILY_ACTIVITIES,
  type DailyKind,
  LOAD_CONDITIONS,
  phaseReference,
  previousFlowReport,
} from '@felix/domain';
import { ActionIcon, Button, Table } from '@mantine/core';
import {
  IconAnchor,
  IconBox,
  IconClock,
  IconMapPin,
  IconPlus,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatHours } from '../../format.js';
import { ltUtcText } from '../../inputs/lt-utc.js';
import ds from './DailySections.module.css';
import dep from './DepartureSections.module.css';
import { previousDaily, previousRunningHours } from './event-form.js';
import {
  ComputedField,
  NoteField,
  NumberField,
  PortField,
  ScrollTable,
  SelectField,
  TextField,
} from './fields.js';
import le from './LegEventScreen.module.css';
import noon from './NoonReportScreen.module.css';
import { Group, LocalDateTimeField } from './NoonSections.js';
import { formatLatitude, formatLongitude } from './noon-report.js';
import { useNumber } from './numbers.js';
import type { VoyageReport } from './types.js';

/**
 * Seferin günlük raporlarının bölümleri (ADR 0059 §3, §4; ortağın `g1.jpeg`): aşama referansı
 * (Berthing / demir / drift başlangıcından, türetilir) ve son rapordan beri süre, konum durumu,
 * ETD, yük ilerlemesi, makine çalışma saatleri. Tüketim ROB tablosunda ("bugün" sütunuyla), hava
 * ortak bölümde. Alanlar `data` altında; doğrulama `parseVoyageReport`.
 */

const useK = () => {
  const { t } = useTranslation();
  return (key: string, options?: Record<string, unknown>) =>
    t(`voyage.daily.${key}`, options ?? {});
};

const pairClass = [noon.pair, le.fieldPair].join(' ');
const round1 = (value: number) => Math.round(value * 10) / 10;

/* ---------- Aşama referansı ve son rapordan beri (türetilir) ---------- */

export function DailyReferencePanel({
  kind,
  reports,
  currentId,
}: {
  kind: DailyKind;
  reports: readonly VoyageReport[];
  currentId: string | null;
}) {
  const k = useK();
  const { t } = useTranslation();
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  const offset = (useWatch({ name: 'tzOffsetMin' }) as number | undefined) ?? 0;
  if (!reportedAt) return null;
  const others = reports.filter((r) => r.id !== currentId && r.status !== 'draft');
  const phase = phaseReference(kind, others, reportedAt);
  const previous = previousFlowReport(others, reportedAt);
  const time = (iso: string | null) => (iso ? ltUtcText(iso, offset) : '—');
  return (
    <Group title={k(`reference.${kind}`)} icon={<IconAnchor size={18} />} className={le.wide}>
      {phase ? (
        <dl className={dep.facts}>
          {phase.kind === 'arrival' ? (
            <>
              <div>
                <dt>{k('reference.allFast')}</dt>
                <dd>{time(phase.allFast)}</dd>
              </div>
              <div>
                <dt>{k('reference.firstLine')}</dt>
                <dd>{time(phase.firstLine)}</dd>
              </div>
              <div>
                <dt>{k('reference.pilotOff')}</dt>
                <dd>{time(phase.pilotOff)}</dd>
              </div>
              <div>
                <dt>{k('reference.tugs')}</dt>
                <dd>{phase.tugs.length > 0 ? phase.tugs.join(' / ') : '—'}</dd>
              </div>
            </>
          ) : (
            <>
              <div>
                <dt>{k(`reference.started.${phase.kind}`)}</dt>
                <dd>{time(phase.reportedAt)}</dd>
              </div>
              <div>
                <dt>{k('reference.position')}</dt>
                <dd>
                  {phase.lat !== null && phase.lon !== null
                    ? `${formatLatitude(phase.lat)} ${formatLongitude(phase.lon)}`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>{k('reference.reason')}</dt>
                <dd>{phase.reason ? t(`voyage.event.options.reasons.${phase.reason}`) : '—'}</dd>
              </div>
            </>
          )}
        </dl>
      ) : (
        <p className={noon.muted}>{k(`reference.none.${kind}`)}</p>
      )}
      <dl className={dep.facts}>
        <div>
          <dt>{k('sinceLast.type')}</dt>
          <dd>{previous ? t(`voyage.kinds.${previous.kind}`) : '—'}</dd>
        </div>
        <div>
          <dt>{k('sinceLast.time')}</dt>
          <dd>{previous ? ltUtcText(previous.reportedAt, previous.tzOffsetMin) : '—'}</dd>
        </div>
        <div>
          <dt>{k('sinceLast.elapsed')}</dt>
          <dd>{previous ? formatHours(previous.hours) : '—'}</dd>
        </div>
      </dl>
    </Group>
  );
}

/* ---------- Konum durumu ---------- */

export function LocationSection({ kind, readOnly }: { kind: DailyKind; readOnly: boolean }) {
  const k = useK();
  const port = kind === 'port_daily';
  const location = useWatch({ name: 'data.location' }) as
    | Record<string, unknown>
    | null
    | undefined;
  // Eski (M15c öncesi) günlükte salt okunur boş bölüm gösterilmez
  if (
    readOnly &&
    !Object.values(location ?? {}).some((v) => v !== null && v !== undefined && v !== '')
  ) {
    return null;
  }
  return (
    <Group title={k('location.title')} icon={<IconMapPin size={18} />} className={le.wide}>
      <div className={pairClass}>
        <PortField
          name="data.location.port"
          codeName="data.location.portCode"
          label={k(`location.port.${kind}`)}
          required={port}
          readOnly={readOnly}
        />
        <TextField
          name="data.location.area"
          label={k(`location.area.${kind}`)}
          readOnly={readOnly}
        />
        <SelectField
          name="data.location.activity"
          label={k('location.activity')}
          required
          readOnly={readOnly}
          options={DAILY_ACTIVITIES.map((value) => ({
            value,
            label: k(`activities.${value}`),
          }))}
        />
        {port ? (
          <SelectField
            name="data.location.berthSide"
            label={k('location.berthSide')}
            readOnly={readOnly}
            options={BERTH_SIDES.map((value) => ({
              value,
              label: k(`berthSides.${value}`),
            }))}
          />
        ) : null}
      </div>
    </Group>
  );
}

/* ---------- ETD ---------- */

export function EtdSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  const values = useWatch({ name: ['data.etd', 'data.etdRemarks'] }) as unknown[];
  if (readOnly && values.every((v) => v === null || v === undefined || v === '')) return null;
  return (
    <Group title={k('etd.title')} icon={<IconClock size={18} />} className={le.wide}>
      <div className={pairClass}>
        <LocalDateTimeField name="data.etd" label={k('etd.etd')} readOnly={readOnly} />
        <TextField name="data.etdRemarks" label={k('etd.remarks')} readOnly={readOnly} />
      </div>
      {readOnly ? null : <p className={noon.hint}>{k('etd.note')}</p>}
    </Group>
  );
}

/* ---------- Yük ilerlemesi ---------- */

export function CargoProgressSection({
  kind,
  reports,
  currentId,
  readOnly,
}: {
  kind: DailyKind;
  reports: readonly VoyageReport[];
  currentId: string | null;
  readOnly: boolean;
}) {
  const k = useK();
  const { t } = useTranslation();
  const n = useNumber();
  const { setValue, getFieldState } = useFormContext();
  const progress = (useWatch({ name: 'data.cargoProgress' }) ?? {}) as Record<string, unknown>;
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  const previous = reportedAt
    ? previousDaily(
        kind,
        reports.filter((r) => r.id !== currentId),
        reportedAt,
      )
    : undefined;
  const base = (previous?.data.cargoProgress as Record<string, unknown> | undefined)
    ?.handledTotalQty;
  const sinceLast = progress.handledSinceLastQty;
  // Toplam = önceki günlüğün toplamı + son rapordan beri (öneri; elle değişir)
  const seen = useRef(sinceLast);
  useEffect(() => {
    if (readOnly || seen.current === sinceLast) return;
    seen.current = sinceLast;
    // Yalnız kullanıcı değiştirince (form yüklenirken kayıtlı toplam korunur)
    const touched = getFieldState('data.cargoProgress.handledSinceLastQty').isDirty;
    if (touched && typeof sinceLast === 'number' && typeof base === 'number') {
      setValue('data.cargoProgress.handledTotalQty', round1(base + sinceLast), {
        shouldDirty: true,
      });
    }
  }, [readOnly, sinceLast, base, setValue, getFieldState]);
  const none = progress.operation === 'none';
  const blank = Object.values(progress).every((v) => v === null || v === undefined || v === '');
  if (readOnly && blank) return null;
  const unit = typeof progress.unit === 'string' ? progress.unit : 'MT';
  return (
    <Group title={k('cargo.title')} icon={<IconBox size={18} />} className={le.wide}>
      <div className={pairClass}>
        <SelectField
          name="data.cargoProgress.loadCondition"
          label={k('cargo.loadCondition')}
          readOnly={readOnly}
          options={LOAD_CONDITIONS.map((value) => ({
            value,
            label: t(`voyage.event.options.loadConditions.${value}`),
          }))}
        />
        <SelectField
          name="data.cargoProgress.operation"
          label={k('cargo.operation')}
          readOnly={readOnly}
          options={CARGO_PORT_OPERATIONS.map((value) => ({
            value,
            label: k(`cargo.operations.${value}`),
          }))}
        />
        {none ? null : (
          <>
            <TextField
              name="data.cargoProgress.cargoType"
              label={k('cargo.cargoType')}
              readOnly={readOnly}
            />
            <SelectField
              name="data.cargoProgress.unit"
              label={k('cargo.unit')}
              readOnly={readOnly}
              options={CARGO_UNITS.map((value) => ({
                value,
                label: t(`voyage.sections.cargo.units.${value}`),
              }))}
            />
            <NumberField
              name="data.cargoProgress.plannedQty"
              label={k('cargo.planned')}
              unit={unit}
              decimals={1}
              readOnly={readOnly}
            />
            <NumberField
              name="data.cargoProgress.handledSinceLastQty"
              label={k('cargo.sinceLast')}
              unit={unit}
              decimals={1}
              readOnly={readOnly}
            />
            <NumberField
              name="data.cargoProgress.handledTotalQty"
              label={k('cargo.total')}
              unit={unit}
              decimals={1}
              readOnly={readOnly}
            />
            <ComputedField
              label={k('cargo.remaining')}
              value={n.unit(cargoRemaining(progress), unit, { digits: 1, fixed: true })}
            />
          </>
        )}
      </div>
      <NoteField
        name="data.cargoProgress.remarks"
        label={k('cargo.remarks')}
        readOnly={readOnly}
        minRows={2}
      />
      {readOnly ? null : <p className={noon.hint}>{k('cargo.note')}</p>}
    </Group>
  );
}

/* ---------- Makine çalışma saatleri ---------- */

const keyOf = (name: unknown) =>
  typeof name === 'string' ? name.trim().toLocaleLowerCase('en') : '';

export function RunningHoursSection({
  kind,
  reports,
  currentId,
  readOnly,
}: {
  kind: DailyKind;
  reports: readonly VoyageReport[];
  currentId: string | null;
  readOnly: boolean;
}) {
  const k = useK();
  const { setValue, getFieldState } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'data.runningHours' });
  const rows = (useWatch({ name: 'data.runningHours' }) ?? []) as Record<string, unknown>[];
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  const totals = reportedAt
    ? previousRunningHours(
        kind,
        reports.filter((r) => r.id !== currentId),
        reportedAt,
      )
    : new Map<string, number>();
  // Toplam = önceki toplam + son rapordan beri (öneri; elle değişir)
  const seen = useRef(rows.map((row) => row.sinceLastH));
  useEffect(() => {
    if (readOnly) return;
    rows.forEach((row, index) => {
      if (seen.current[index] === row.sinceLastH) return;
      const before = totals.get(keyOf(row.equipment));
      const touched = getFieldState(`data.runningHours.${index}.sinceLastH`).isDirty;
      if (touched && typeof row.sinceLastH === 'number' && before !== undefined) {
        setValue(`data.runningHours.${index}.totalH`, round1(before + row.sinceLastH), {
          shouldDirty: true,
        });
      }
    });
    seen.current = rows.map((row) => row.sinceLastH);
  }, [readOnly, rows, totals, setValue, getFieldState]);
  if (readOnly && fields.length === 0) return null;
  const columns = readOnly ? 5 : 6;
  return (
    <Group title={k('hours.title')} icon={<IconSettings size={18} />} className={le.wide}>
      <ScrollTable label={k('hours.title')} framed={false}>
        <Table className={[noon.table, ds.hoursTable].join(' ')} data-cells withColumnBorders>
          <colgroup>
            <col className={ds.colEquipment} />
            <col className={ds.colHours} />
            <col className={ds.colHours} />
            <col className={ds.colHours} />
            <col />
            {readOnly ? null : <col className={ds.colAction} />}
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{k('hours.equipment')}</Table.Th>
              <Table.Th data-numeric>{k('hours.sinceLast')}</Table.Th>
              <Table.Th data-numeric>{k('hours.today')}</Table.Th>
              <Table.Th data-numeric>{k('hours.total')}</Table.Th>
              <Table.Th>{k('hours.remarks')}</Table.Th>
              {readOnly ? null : (
                <Table.Th>
                  <span className={noon.srOnly}>{k('hours.remove')}</span>
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {fields.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columns} className={noon.muted}>
                  {k('hours.none')}
                </Table.Td>
              </Table.Tr>
            ) : null}
            {fields.map((row, index) => {
              const base = `data.runningHours.${index}`;
              const label = (key: string) => `${k(`hours.${key}`)} ${index + 1}`;
              return (
                <Table.Tr key={row.id} className={noon.editRow}>
                  <Table.Td>
                    <TextField
                      name={`${base}.equipment`}
                      label={k('hours.equipment')}
                      ariaLabel={label('equipment')}
                      layout="cell"
                      required
                      readOnly={readOnly}
                    />
                  </Table.Td>
                  {(['sinceLastH', 'todayH', 'totalH'] as const).map((key) => (
                    <Table.Td key={key} data-numeric>
                      <NumberField
                        name={`${base}.${key}`}
                        label={k(`hours.${key}`)}
                        ariaLabel={label(key)}
                        layout="cell"
                        readOnly={readOnly}
                        decimals={1}
                      />
                    </Table.Td>
                  ))}
                  <Table.Td>
                    <TextField
                      name={`${base}.remarks`}
                      label={k('hours.remarks')}
                      ariaLabel={label('remarks')}
                      layout="cell"
                      readOnly={readOnly}
                    />
                  </Table.Td>
                  {readOnly ? null : (
                    <Table.Td className={noon.actionCell}>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={label('remove')}
                        className={noon.rowAction}
                        onClick={() => remove(index)}
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollTable>
      {readOnly ? null : (
        <>
          <Button
            variant="subtle"
            size="xs"
            className={noon.addButton}
            leftSection={<IconPlus size={14} />}
            onClick={() =>
              append({ equipment: '', sinceLastH: null, todayH: null, totalH: null, remarks: null })
            }
          >
            {k('hours.add')}
          </Button>
          <p className={noon.hint}>{k('hours.note')}</p>
        </>
      )}
    </Group>
  );
}
